import { ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import AppShellSkeleton from './AppShellSkeleton';
import { APP_SHELL_MIN_DISPLAY_MS } from '@hooks/useMinimumLoadingDuration';

type AppShellSuspenseProps = {
  children: ReactNode;
  minMs?: number;
};

type SuspendTrackerProps = {
  suspendedAtRef: React.MutableRefObject<number | null>;
};

function SuspendTracker({ suspendedAtRef }: SuspendTrackerProps) {
  if (suspendedAtRef.current === null) {
    suspendedAtRef.current = Date.now();
  }

  return <AppShellSkeleton />;
}

type MinDurationContentProps = {
  children: ReactNode;
  minMs: number;
  suspendedAtRef: React.MutableRefObject<number | null>;
};

function MinDurationContent({
  children,
  minMs,
  suspendedAtRef
}: MinDurationContentProps) {
  const [revealed, setRevealed] = useState(
    () => suspendedAtRef.current === null
  );

  useEffect(() => {
    const startedAt = suspendedAtRef.current;

    if (startedAt === null) {
      setRevealed(true);
      return;
    }

    const remaining = minMs - (Date.now() - startedAt);

    if (remaining <= 0) {
      suspendedAtRef.current = null;
      setRevealed(true);
      return;
    }

    const timer = setTimeout(() => {
      suspendedAtRef.current = null;
      setRevealed(true);
    }, remaining);

    return () => clearTimeout(timer);
  }, [minMs, suspendedAtRef]);

  return revealed ? <>{children}</> : <AppShellSkeleton />;
}

export function AppShellSuspense({
  children,
  minMs = APP_SHELL_MIN_DISPLAY_MS
}: AppShellSuspenseProps) {
  const suspendedAtRef = useRef<number | null>(null);

  return (
    <Suspense fallback={<SuspendTracker suspendedAtRef={suspendedAtRef} />}>
      <MinDurationContent minMs={minMs} suspendedAtRef={suspendedAtRef}>
        {children}
      </MinDurationContent>
    </Suspense>
  );
}
